import React, { Component } from 'react';
import { connect } from 'react-redux';
import axios from 'axios';
import { Container, Form } from 'react-bootstrap';
import { withRouter } from '../withRouter';
import { joinAsMirror } from 'actions/room';
import DotLoader from 'components/Utlility/DotLoader';
import { RoomErrorCodes } from 'constants/ErrorCodes';

import formStyles from "components/Forms/FormStyles.module.scss";
import styles from "./MirrorJoin.module.scss";
import FormAlert from 'components/Forms/FormAlert';

class MirrorJoin extends Component {
    static displayName = MirrorJoin.name;

    constructor(props) {
        super(props);

        this.state = {
            roomId: "",
            loading: false,
            errorMessage: "",
            canJoin: false,
            resolvedRoomId: null, // the internal room ID from game-exists check
            passwordProtected: false,
            pin: "",
        };
        this.cancelToken = null;
    }

    async componentDidMount() {
        // If already in a mirror room, go to lobby
        if (this.props.room.roomOpen && this.props.room.isMirror) {
            return this.props.navigate("/lobby");
        }

        const queryParams = new URLSearchParams(window.location.search);

        // If the user was sent here after a terminal disconnect (kicked,
        // Connect disabled, session full, etc.) the originating Lobby.js
        // appends ?error=<closeCode>. Surface the friendly copy on the
        // form so they understand why they're back at the join screen.
        if (queryParams.has("error")) {
            const errCode = Number(queryParams.get("error"));
            const errCopy = RoomErrorCodes[errCode]?.displayMessage;
            if (errCopy) this.setState({ errorMessage: errCopy });
        }

        if (queryParams.has("code")) {
            const code = queryParams.get("code").toUpperCase();
            this.setState({ roomId: code });
            if (code.length === 4) {
                const result = await this.checkGameExists(code);
                // Mirror equivalent of Join.js's ?qrCode= auto-join: when the URL
                // arrives with ?code=ABCD (copied via the Connect button in
                // PlayerList) and the room exists + isn't PIN-protected, skip
                // the form and join immediately. PIN-protected rooms fall
                // through to the form so the user can enter the PIN.
                if (result?.gameExists && !result?.passwordProtected) {
                    this.autoJoinMirror(code);
                }
            }
        }
    }

    /**
     * Auto-join helper for ?code=ABCD URLs. Takes the room ID directly rather
     * than reading from state — the caller has just validated it via
     * checkGameExists, and setState batching means this.state.canJoin /
     * resolvedRoomId aren't reflected yet on the same microtask.
     */
    autoJoinMirror = async (roomId) => {
        if (this.state.loading) return;
        this.setState({ loading: true, errorMessage: "" });
        const result = await this.props.joinAsMirror(roomId, null);
        if (result?.Status === "Error") {
            this.setState({
                loading: false,
                errorMessage: result.Message || "Failed to join as mirror.",
            });
        }
        // On success, componentDidUpdate navigates to /lobby
    };

    componentDidUpdate(prevProps) {
        if (this.props.room.roomOpen === true && prevProps.room.roomOpen === false) {
            this.props.navigate("/lobby");
        }
    }

    onChangeRoomId = async (e) => {
        this.setState({ errorMessage: "" });
        const roomId = e.target.value.toUpperCase();

        if (roomId.length === 4) {
            this.setState({ roomId });
            await this.checkGameExists(roomId);
        } else {
            this.setState({ canJoin: false, roomId, resolvedRoomId: null, passwordProtected: false, pin: "" });
        }
    };

    onChangePIN = (e) => {
        this.setState({ errorMessage: "", pin: e.target.value });
    };

    async checkGameExists(roomId) {
        if (this.cancelToken != null) {
            this.cancelToken.cancel("Operation canceled due to new request.");
        }

        this.cancelToken = axios.CancelToken.source();

        return await axios.get(
            process.env.REACT_APP_MATCH_MAKE_URL + `/game-exists/?roomId=${roomId}&sessionId=`,
            { cancelToken: this.cancelToken.token }
        ).then(res => {
            if (res.data.gameExists) {
                this.setState({
                    canJoin: true,
                    errorMessage: "",
                    resolvedRoomId: roomId,
                    passwordProtected: res.data.passwordProtected || false,
                });
                if (res.data.passwordProtected) {
                    this.setState({ errorMessage: "Please enter room PIN", });
                }
            } else {
                this.setState({
                    canJoin: false,
                    errorMessage: `Room '${roomId}' does not exist...`,
                    resolvedRoomId: null,
                    passwordProtected: false,
                });
            }
            this.cancelToken = null;
            return res.data;
        }).catch((error) => {
            if (axios.isCancel(error)) {
                console.log('Request canceled');
            }
            this.setState({ canJoin: false, errorMessage: "Connection error. Please try again." });
            return null;
        });
    }

    clickJoinMirror = async (e) => {
        e.preventDefault();
        if (!this.state.canJoin || this.state.loading) return;

        if (this.state.passwordProtected && this.state.pin.length === 0) {
            this.setState({ errorMessage: "Please enter room PIN" });
            return;
        }

        this.setState({ loading: true, errorMessage: "" });

        const password = this.state.passwordProtected ? this.state.pin : null;
        const result = await this.props.joinAsMirror(this.state.resolvedRoomId, password);
        if (result.Status === "Error") {
            this.setState({
                loading: false,
                errorMessage: result.Message || "Failed to join as mirror.",
            });
        }
        // On success, componentDidUpdate navigates to /lobby
    };

    render() {
        return (
            <Container>
                <div>
                    <div className={styles.headerWrap}>
                        <div className={styles.hero}>
                            <svg className={styles.heroIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
                                <line x1="8" y1="21" x2="16" y2="21" />
                                <line x1="12" y1="17" x2="12" y2="21" />
                            </svg>
                            <h2 className={formStyles.formTitle}>Game.City Connect</h2>
                            <p className={styles.tagline}>Bring the game to any screen</p>
                            <p className={styles.intro}>
                                Joining a friend or family member's game from somewhere else? You're in the right place. Drop in the room code below and you'll see the game on your screen - they keep the host TV at theirs, and everyone plays along together.
                            </p>
                        </div>
                    </div>

                    <div className={styles.steps}>
                        <div className={styles.step}>
                            <div className={styles.stepNumber}>STEP 1</div>
                            <svg className={styles.stepIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
                            </svg>
                            <span className={styles.stepLabel}>Ask the host for the room code. They'll see it at the top of their game.</span>
                        </div>
                        <div className={styles.step}>
                            <div className={styles.stepNumber}>STEP 2</div>
                            <svg className={styles.stepIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                <rect x="2" y="6" width="20" height="12" rx="2" ry="2" />
                                <line x1="6" y1="10" x2="6" y2="10" />
                                <line x1="10" y1="10" x2="10" y2="10" />
                                <line x1="14" y1="10" x2="14" y2="10" />
                                <line x1="18" y1="10" x2="18" y2="10" />
                                <line x1="6" y1="14" x2="18" y2="14" />
                            </svg>
                            <span className={styles.stepLabel}>Enter the code below</span>
                        </div>
                        <div className={styles.step}>
                            <div className={styles.stepNumber}>STEP 3</div>
                            <svg className={styles.stepIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
                                <line x1="8" y1="21" x2="16" y2="21" />
                                <line x1="12" y1="17" x2="12" y2="21" />
                                <polyline points="9 9 11 11 15 7" />
                            </svg>
                            <span className={styles.stepLabel}>Game on. Their game appears on your screen. Grab your phone, scan the QR code, and you're in.</span>
                        </div>
                    </div>

                    <Form onSubmit={this.clickJoinMirror}>
                        <Form.Group className="mb-3">
                            <Form.Label>Room Code</Form.Label>
                            <Form.Control
                                type="text"
                                autoComplete="off"
                                maxLength={4}
                                placeholder="ABCD"
                                className={styles.codeInput}
                                value={this.state.roomId}
                                required
                                onChange={this.onChangeRoomId}
                            />
                            <FormAlert variant="danger">{this.state.errorMessage}</FormAlert>
                        </Form.Group>
                        {
                            this.state.passwordProtected &&
                            <Form.Group className="mb-3">
                                <Form.Label>Room PIN</Form.Label>
                                <Form.Control
                                    type="password"
                                    autoComplete="off"
                                    placeholder="Enter Room PIN"
                                    style={{ textTransform: "uppercase" }}
                                    value={this.state.pin}
                                    maxLength={8}
                                    minLength={4}
                                    required
                                    onChange={this.onChangePIN}
                                />
                            </Form.Group>
                        }
                        <button
                            className={`${formStyles.button} ${styles.connectBtn}`}
                            disabled={!this.state.canJoin}
                            type="submit"
                        >
                            {this.state.loading ? <DotLoader size="medium" variant="light" /> : "Connect"}
                        </button>
                        <p className={styles.connectFootnote}>
                            Up to 8 households can join the same game. No app, no download - works on any screen.
                        </p>
                    </Form>
                </div>
            </Container>
        );
    }
}

const mapStateToProps = (state) => ({
    room: state.room,
});

const mapDispatchToProps = (dispatch) => ({
    joinAsMirror: (roomId, password) => dispatch(joinAsMirror(roomId, password)),
});

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(MirrorJoin));
